import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Sun, Droplets, Moon } from "lucide-react";
import SiteShell from "../../components/layout/SiteShell";
import SEO from "../../components/seo/SEO";
import ConversionCtas from "../../components/content/ConversionCtas";
import WellnessDisclaimer from "../../components/content/WellnessDisclaimer";
import { HOME_CTA_DARK_OUTLINE, HOME_CTA_DARK_PRIMARY } from "../../lib/homeCta";
import { getLifestyleImage } from "../../lib/lifestyleImages";
import { HERO_HOME_IMAGE } from "../../lib/data";

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-48px" },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

const moments = [
  {
    id: "morning",
    icon: Sun,
    label: "Morning",
    title: "Fridge → spoon → bowl",
    copy: "Keep the jar cold. Add a small spoonful to oats, citrus juice, or a dairy-free milk you already drink. Taste first; scale later.",
    tip: "Stir into warm (not boiling) liquid so the gel stays silky. Consistency beats quantity.",
    imageKey: "routineMorning",
  },
  {
    id: "midday",
    icon: Droplets,
    label: "Midday",
    title: "Blend it into what you already make",
    copy: "A spoonful in a home smoothie or protein shake fits an active day without a second supplement ritual. Fruit and extras stay in your blender — not in our jars.",
    tip: "Blend cold gel last for 10–15 seconds. It thickens gently; start with half a spoon if texture is new to you.",
    imageKey: "routineSmoothie",
  },
  {
    id: "evening",
    icon: Moon,
    label: "Evening",
    title: "Quiet kitchen, ready for tomorrow",
    copy: "Wipe the rim, clean spoon, lid on, back in the fridge. Tomorrow’s pour is already waiting — no sachets, no prep stack.",
    tip: "Label the open date on the lid. Most people keep opened gel fridge-first and use it within a few weeks.",
    imageKey: "routineKitchen",
  },
];

const steps = [
  {
    n: "01",
    title: "Start small",
    text: "Half a spoonful is enough to learn the texture. Sea moss is a food-style stir-in, not a dosing protocol.",
  },
  {
    n: "02",
    title: "Pair with a habit you keep",
    text: "Shake, oats, tea off the boil, or coconut water — attach it to something you already do on training or walk days.",
  },
  {
    n: "03",
    title: "Keep it fridge-ready",
    text: "Cold jar, clean utensil, tight lid. Fresh gel is meant to live in the kitchen, not the gym bag.",
  },
];

function MomentCard({ moment, open, onToggle }) {
  const img = getLifestyleImage(moment.imageKey);
  const Icon = moment.icon;

  return (
    <motion.article
      {...fadeUp}
      className="group relative overflow-hidden rounded-[1.75rem] border border-white/14 bg-panel/70 transition-all duration-500 hover:border-gold/40 hover:-translate-y-1 hover:shadow-[0_24px_48px_-28px_rgba(0,0,0,0.55)]"
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left"
        aria-expanded={open}
        aria-controls={`tip-${moment.id}`}
      >
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={img.src}
            alt={img.alt}
            width={img.width}
            height={img.height}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover brightness-[1.12] contrast-[1.02] saturate-[1.02] transition-transform duration-700 ease-out group-hover:scale-[1.045]"
          />
          <div
            className="absolute inset-0 bg-gradient-to-t from-[#0c1410]/78 via-[#0c1410]/18 to-transparent"
            aria-hidden
          />
          <span className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-void/45 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-ivory/95 backdrop-blur-md">
            <Icon className="h-3.5 w-3.5 text-gold" strokeWidth={1.75} aria-hidden />
            {moment.label}
          </span>
        </div>

        <div className="px-5 pb-5 pt-5 md:px-6 md:pb-6">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-sans font-semibold text-xl text-ivory m-0 leading-snug">{moment.title}</h3>
            <span
              className={`mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/18 bg-white/[0.06] text-sage transition-transform duration-300 ${
                open ? "rotate-180 border-gold/40 text-gold" : "group-hover:border-gold/35"
              }`}
              aria-hidden
            >
              <ChevronDown className="h-4 w-4" strokeWidth={1.75} />
            </span>
          </div>
          <p className="mt-3 text-sm text-sage leading-relaxed m-0">{moment.copy}</p>
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            id={`tip-${moment.id}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="mx-5 mb-5 rounded-2xl border border-gold/25 bg-gold/10 px-4 py-3.5 md:mx-6 md:mb-6">
              <p className="text-[10px] uppercase tracking-[0.22em] text-gold m-0">Kitchen tip</p>
              <p className="mt-2 text-sm text-ivory/90 leading-relaxed m-0">{moment.tip}</p>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.article>
  );
}

export default function SeaMossActiveRoutinesPage() {
  const path = "/learn/sea-moss-for-active-routines";
  const hero = getLifestyleImage("heroLifestyle");
  const coast = getLifestyleImage("routineCoastal");
  const [openId, setOpenId] = useState("morning");
  const [revealed, setRevealed] = useState(1);

  return (
    <SiteShell theme="dark">
      <SEO
        title="Sea moss for active routines | Fridge-ready kitchen habits | Moss Matrix"
        description="Simple ways to fold fridge-ready sea moss gel into everyday active routines — morning spoons, home smoothies, and clean kitchen habits. Calm, practical, non-medical."
        path={path}
      />

      {/* Full-bleed hero — lighter overlays so lifestyle photography reads as health-brand fresh */}
      <header className="relative min-h-[88svh] md:min-h-[92svh] w-full overflow-hidden">
        <img
          src={hero.src}
          alt={hero.alt}
          width={hero.width}
          height={hero.height}
          fetchPriority="high"
          className="absolute inset-0 h-full w-full object-cover brightness-[1.14] contrast-[1.02] saturate-[1.05]"
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-[#0c1410]/58 via-[#0c1410]/28 to-[#0c1410]/18"
          aria-hidden
        />
        <div
          className="absolute inset-x-0 bottom-0 h-[48%] bg-gradient-to-t from-[#0c1410] via-[#0c1410]/55 to-transparent"
          aria-hidden
        />

        <div className="relative z-10 flex min-h-[88svh] md:min-h-[92svh] flex-col justify-end px-6 md:px-10 pb-14 md:pb-20 pt-28">
          <div className="max-w-7xl mx-auto w-full">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="home-eyebrow m-0 mb-3 text-sage/95"
            >
              Learn · Active routines
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.06 }}
              className="font-sans font-semibold text-4xl sm:text-5xl md:text-6xl text-ivory m-0 tracking-tight max-w-3xl leading-[1.06]"
            >
              Sea moss for days you already move.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.12 }}
              className="mt-5 text-base md:text-lg text-sage/95 m-0 max-w-xl leading-relaxed"
            >
              Fridge-ready gel that slips into kitchen habits — spoons, smoothies, shakes you make at home — without turning wellness into homework.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.2 }}
              className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3"
            >
              <a href="#routine-moments" className={`${HOME_CTA_DARK_PRIMARY} min-h-[52px] px-8 py-3.5`}>
                See the moments
              </a>
              <Link to="/shop" className={`${HOME_CTA_DARK_OUTLINE} min-h-[52px] px-8 py-3.5`}>
                Shop Moss Matrix
              </Link>
            </motion.div>
          </div>
        </div>
      </header>

      <main className="relative -mt-4 md:-mt-8 pb-24 md:pb-32 mm-dark-content">
        {/* Short intro — one job */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-14" aria-labelledby="intro-heading">
          <motion.div {...fadeUp} className="grid lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-14 items-center">
            <div>
              <h2 id="intro-heading" className="font-sans font-semibold text-2xl md:text-3xl text-ivory m-0 tracking-tight">
                Active does not mean complicated.
              </h2>
              <p className="mt-4 text-base text-sage leading-relaxed max-w-xl m-0">
                People with walk days, training days, or simply busy kitchens often want one fridge jar they can trust —
                not a performance stack. Moss Matrix gel is made fresh for UK delivery: herb-based blends, clean spoons,
                and a rhythm that fits real counters.
              </p>
              <p className="mt-4 text-sm text-sage/85 leading-relaxed max-w-xl m-0">
                This guide is practical habit framing only — not medical advice, not a substitute for sleep, protein, or professional care.
              </p>
            </div>
            <Link
              to="/products/gold-sea-moss-gel"
              className="group relative block overflow-hidden rounded-[1.75rem] border border-white/14 bg-panel/60 transition-all duration-500 hover:border-gold/45 hover:-translate-y-1"
            >
              <div className="aspect-[4/3] relative">
                <img
                  src={HERO_HOME_IMAGE}
                  alt="Moss Matrix sea moss gel jar and pouch with botanicals"
                  className="absolute inset-0 h-full w-full object-cover object-center brightness-[1.08] transition-transform duration-700 group-hover:scale-[1.04]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0c1410]/70 via-transparent to-transparent" aria-hidden />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <p className="text-[10px] uppercase tracking-[0.2em] text-gold m-0">Product</p>
                <p className="mt-1 font-sans font-semibold text-lg text-ivory m-0">Fresh gel, fridge-first</p>
              </div>
            </Link>
          </motion.div>
        </section>

        {/* Routine moments — interactive */}
        <section
          id="routine-moments"
          className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-20"
          aria-labelledby="moments-heading"
        >
          <motion.div {...fadeUp} className="max-w-2xl mb-8 md:mb-10">
            <h2 id="moments-heading" className="font-sans font-semibold text-2xl md:text-3xl text-ivory m-0 tracking-tight">
              Three moments in an active day
            </h2>
            <p className="mt-3 text-sm md:text-base text-sage leading-relaxed m-0">
              Tap a card for a short kitchen tip. Coastal mornings, blender mid-days, tidy evenings — no gym-bag mythology.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5 md:gap-6">
            {moments.map((m) => (
              <MomentCard
                key={m.id}
                moment={m}
                open={openId === m.id}
                onToggle={() => setOpenId((prev) => (prev === m.id ? null : m.id))}
              />
            ))}
          </div>
        </section>

        {/* Step reveal */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-20" aria-labelledby="steps-heading">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <motion.div {...fadeUp}>
              <h2 id="steps-heading" className="font-sans font-semibold text-2xl md:text-3xl text-ivory m-0 tracking-tight">
                A simple stir-in rhythm
              </h2>
              <p className="mt-3 text-sm md:text-base text-sage leading-relaxed max-w-md m-0">
                Reveal each step when you are ready. Nothing here is a prescription — just a calm order of operations.
              </p>

              <ol className="mt-8 space-y-3 list-none p-0 m-0">
                {steps.map((s, i) => {
                  const visible = i < revealed;
                  return (
                    <li key={s.n}>
                      <button
                        type="button"
                        onClick={() => setRevealed(Math.max(revealed, i + 1))}
                        className={`w-full text-left rounded-2xl border px-5 py-4 transition-all duration-500 ${
                          visible
                            ? "border-gold/30 bg-panel/90 hover:border-gold/50"
                            : "border-white/10 bg-white/[0.03] hover:border-white/20"
                        }`}
                      >
                        <div className="flex items-baseline gap-4">
                          <span className={`font-sans text-sm tracking-widest ${visible ? "text-gold" : "text-sage/50"}`}>
                            {s.n}
                          </span>
                          <span className={`font-sans font-semibold text-lg ${visible ? "text-ivory" : "text-sage/60"}`}>
                            {s.title}
                          </span>
                        </div>
                        <AnimatePresence>
                          {visible ? (
                            <motion.p
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="mt-2 ml-10 text-sm text-sage leading-relaxed m-0 overflow-hidden"
                            >
                              {s.text}
                            </motion.p>
                          ) : (
                            <p className="mt-2 ml-10 text-xs text-sage/45 m-0">Tap to reveal</p>
                          )}
                        </AnimatePresence>
                      </button>
                    </li>
                  );
                })}
              </ol>

              {revealed < steps.length ? (
                <button
                  type="button"
                  onClick={() => setRevealed(steps.length)}
                  className="mt-5 text-xs uppercase tracking-[0.2em] text-gold hover:text-goldhover transition"
                >
                  Reveal all steps →
                </button>
              ) : (
                <Link
                  to="/learn/how-to-use-sea-moss-gel"
                  className="mt-5 inline-block text-xs uppercase tracking-[0.2em] text-gold hover:text-goldhover transition"
                >
                  Full how-to guide →
                </Link>
              )}
            </motion.div>

            <motion.div
              {...fadeUp}
              className="relative overflow-hidden rounded-[1.75rem] border border-white/14 group"
            >
              <img
                src={coast.src}
                alt={coast.alt}
                width={coast.width}
                height={coast.height}
                loading="lazy"
                className="w-full aspect-[4/5] object-cover brightness-[1.1] transition-transform duration-700 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0c1410]/65 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <p className="text-[10px] uppercase tracking-[0.2em] text-gold m-0">Atmosphere</p>
                <p className="mt-2 font-sans font-semibold text-xl text-ivory m-0 max-w-xs leading-snug">
                  Coastal freshness — the mood behind a fridge-ready habit.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Soft honesty strip */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-16">
          <motion.div
            {...fadeUp}
            className="rounded-[1.75rem] border border-white/12 bg-gradient-to-br from-panel/90 via-panel/60 to-void/80 px-6 py-8 md:px-10 md:py-10"
          >
            <h2 className="font-sans font-semibold text-xl md:text-2xl text-ivory m-0">What this is not</h2>
            <ul className="mt-4 grid sm:grid-cols-3 gap-4 list-none p-0 m-0">
              {[
                "Not a workout fuel claim or performance stack.",
                "Not medical advice or thyroid guidance.",
                "Not a replacement for meals, hydration, or rest.",
              ].map((line) => (
                <li
                  key={line}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-4 text-sm text-sage leading-relaxed"
                >
                  {line}
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <WellnessDisclaimer compact />
            </div>
          </motion.div>
        </section>

        {/* Soft CTA */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-16" aria-labelledby="cta-heading">
          <motion.div
            {...fadeUp}
            className="relative overflow-hidden rounded-[2rem] border border-gold/25"
          >
            <img
              src={getLifestyleImage("routineKitchen").src}
              alt=""
              className="absolute inset-0 h-full w-full object-cover brightness-[1.08]"
              aria-hidden
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0c1410]/88 via-[#0c1410]/72 to-[#0c1410]/45" aria-hidden />
            <div className="relative z-10 px-6 py-12 md:px-12 md:py-16 max-w-xl">
              <p className="text-[10px] uppercase tracking-[0.22em] text-gold m-0">Ready when you are</p>
              <h2 id="cta-heading" className="mt-3 font-sans font-semibold text-3xl md:text-4xl text-ivory m-0 leading-tight">
                Start with plain Gold or Purple.
              </h2>
              <p className="mt-4 text-sm md:text-base text-sage leading-relaxed m-0">
                Choose your sea moss, keep it simple, and explore mix-in ideas for inspiration. Fresh UK gel — made to order, fridge-first.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
                <Link to="/shop" className={`${HOME_CTA_DARK_PRIMARY} min-h-[48px] px-7 py-3`}>
                  Shop Moss Matrix
                </Link>
                <Link to="/mix-ins" className={`${HOME_CTA_DARK_OUTLINE} min-h-[48px] px-7 py-3`}>
                  Explore mix-in ideas
                </Link>
              </div>
            </div>
          </motion.div>
        </section>

        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <ConversionCtas />
        </div>

        <p className="text-sm text-sage/80 text-center mt-10 px-6">
          <Link to="/learn/how-to-use-sea-moss-gel" className="underline hover:text-goldhover">
            How to use
          </Link>
          {" · "}
          <Link to="/learn/how-to-store-sea-moss-gel" className="underline hover:text-goldhover">
            Storage
          </Link>
          {" · "}
          <Link to="/learn" className="underline hover:text-goldhover">
            Learn hub
          </Link>
          {" · "}
          <Link to="/mix-ins" className="underline hover:text-goldhover">
            Mix-ins
          </Link>
        </p>
      </main>
    </SiteShell>
  );
}
